import { useContext, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { AuthContext } from '../../store/AuthContext';
import { getUserScope, vigilanteVisible, cambioTurnoVisible } from '../../shared/utils/roleScope';
import { escucharSolicitudesServicio } from '../../pages/single/sections/solicitudesService';

// Estados considerados "pendientes de gestión" en cada bandeja.
const CAMBIO_PENDIENTE = 'pendiente_inspector';

// ¿La solicitud (Portal del Empleado) entra en el scope del usuario? Réplica
// de la lógica de `Solicitudes.jsx` sobre los campos denormalizados del doc.
const solicitudEnScope = (s, scope) => {
  if (scope.isAll) return true;
  if (scope.isInspector && Array.isArray(s.inspectorCods)
    && s.inspectorCods.some((c) => scope.inspectorCods.includes(String(c)))) {
    return true;
  }
  // Inspector y administrativo también por delegación + empresa (cubre
  // trabajadores sin servicio/tienda asignado, sin inspectorCod que casar).
  if (scope.isAdministrativo || scope.isInspector) {
    const vigSintetico = {
      delegacion: s.vigilanteDelegacion,
      codEmpresa: s.vigilanteEmpresa,
      departamentoFreematica: s.vigilanteDepartamento,
    };
    if (vigilanteVisible(vigSintetico, scope)) return true;
  }
  return false;
};

/**
 * Cuenta en tiempo real las solicitudes pendientes visibles para el usuario,
 * en las dos bandejas del menú lateral. Aplica el mismo scope por rol que las
 * páginas /solicitudes y /cambios-turno.
 *
 * @return {{ solicitudes: number, cambiosTurno: number }}
 */
export function usePendientesSidebar() {
  const { currentService, adminData } = useContext(AuthContext);
  const scope = useMemo(() => getUserScope(adminData), [adminData]);

  const [solicitudesPend, setSolicitudesPend] = useState(0);
  const [cambiosPend, setCambiosPend] = useState(0);

  // Solicitudes del Portal del Empleado (permiso/vacaciones/dieta/comunicado).
  useEffect(() => {
    if (!currentService?.id) {
      setSolicitudesPend(0);
      return undefined;
    }
    const unsub = escucharSolicitudesServicio(currentService.id, (docs) => {
      const n = docs.filter(
        (s) => (s.estado === 'pendiente' || !s.estado) && solicitudEnScope(s, scope),
      ).length;
      setSolicitudesPend(n);
    }, () => setSolicitudesPend(0));
    return () => unsub();
  }, [currentService?.id, scope]);

  // Cambios de turno pendientes de aprobación del inspector.
  useEffect(() => {
    if (!currentService?.id) {
      setCambiosPend(0);
      return undefined;
    }
    const ref = collection(db, 'servicios', currentService.id, 'solicitudesCambioTurno');
    const q = (scope.isInspector && !scope.isAll && scope.inspectorCods.length > 0)
      ? query(ref, where('inspectorCods', 'array-contains-any', scope.inspectorCods.slice(0, 10)))
      : query(ref);
    const unsub = onSnapshot(q, (snap) => {
      const n = snap.docs
          .map((d) => d.data())
          .filter((s) => s.estado === CAMBIO_PENDIENTE && cambioTurnoVisible(s, scope))
          .length;
      setCambiosPend(n);
    }, () => setCambiosPend(0));
    return () => unsub();
  }, [currentService?.id, scope]);

  return { solicitudes: solicitudesPend, cambiosTurno: cambiosPend };
}
